import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';
import Badge from '../components/Badge.jsx';

// Admin-only page. The /api/users endpoints are server-side admin-
// gated already; the front-end guard here just prevents Mary (or
// anyone non-admin) from seeing a broken page if they nav to the
// URL directly. The 403 from the server is the real defence.

const PASSWORD_MIN = 8;

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

export default function UserManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);     // user object | null
  const [resetting, setResetting] = useState(null); // user object | null
  const [deleting, setDeleting] = useState(null);   // user object | null

  // Front-end guard. The server-side requireAdmin middleware is the
  // real one — this just keeps non-admins out of a UI that would
  // 403 on every API call anyway.
  useEffect(() => {
    if (user && user.is_admin === false) {
      navigate('/settings', { replace: true });
    }
  }, [user, navigate]);

  async function load() {
    setLoading(true);
    try {
      const res = await axios.get('/api/users');
      setUsers(res.data?.users || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  return (
    <section className="page">
      <div className="page-header">
        <Link to="/settings" className="btn-secondary">← Back to Settings</Link>
      </div>

      <div className="record-header" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="record-title" style={{ flex: 1 }}>
          <h2>User Management</h2>
          <div className="record-meta">
            {users.length} user{users.length === 1 ? '' : 's'} ·{' '}
            {users.filter((u) => u.role === 'admin').length} admin ·{' '}
            {users.filter((u) => u.role === 'staff').length} staff ·{' '}
            {users.filter((u) => u.role === 'salesperson').length} salesperson
          </div>
        </div>
        <div className="record-actions">
          <button type="button" className="btn-primary" onClick={() => setAddOpen(true)}>
            + Add User
          </button>
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Title</th>
              <th>Role</th>
              <th>Updated</th>
              <th className="th-actions" />
            </tr>
          </thead>
          <tbody>
            {!loading && users.length === 0 && (
              <tr><td colSpan={6} className="empty-row">No users.</td></tr>
            )}
            {users.map((u) => (
              <tr key={u.id} className={u.role === 'admin' ? 'row-selected' : ''}>
                <td>
                  <div className="cell-primary">{u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim()}</div>
                </td>
                <td className="cell-mono">{u.email}</td>
                <td>{u.title || ''}</td>
                <td>
                  {u.role === 'admin'       && <Badge tone="teal">Admin</Badge>}
                  {u.role === 'staff'       && <Badge tone="blue">Staff</Badge>}
                  {u.role === 'salesperson' && <Badge tone="orange">Salesperson</Badge>}
                  {!u.role                  && <span className="cell-muted">—</span>}
                </td>
                <td className="cell-mono">{formatDateTime(u.updated_at || u.created_at)}</td>
                <td className="contact-row-actions">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => setEditing(u)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => setResetting(u)}
                  >
                    Reset Password
                  </button>
                  {/* Delete: disabled when looking at your own row so
                      an admin can't lock themselves out. Server also
                      refuses (and additionally refuses if this would
                      be the last admin). */}
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={u.id === user?.id}
                    title={u.id === user?.id
                      ? "You can't delete your own account"
                      : `Delete ${u.name || u.email}`}
                    onClick={() => setDeleting(u)}
                    style={{ color: u.id === user?.id ? undefined : 'var(--color-bad, #b91c1c)' }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {addOpen && (
        <UserFormDialog
          mode="create"
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); load(); }}
        />
      )}
      {editing && (
        <UserFormDialog
          mode="edit"
          initial={editing}
          currentUserId={user?.id}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {resetting && (
        <ResetPasswordDialog
          user={resetting}
          onClose={() => setResetting(null)}
          onSaved={() => setResetting(null)}
        />
      )}
      {deleting && (
        <DeleteUserDialog
          user={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); load(); }}
        />
      )}
    </section>
  );
}

// Create + Edit share this form. Mode controls the password field
// (required on create, hidden on edit — passwords are reset via
// the dedicated dialog) and the request method.
function UserFormDialog({ mode, initial, currentUserId, onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState(() => ({
    first_name: initial?.first_name || '',
    last_name:  initial?.last_name  || '',
    email:      initial?.email      || '',
    title:      initial?.title      || '',
    calendly_url: initial?.calendly_url || '',
    role:       initial?.role       || 'staff',
    password:   ''
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  function update(field) {
    return (e) => {
      const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [field]: v }));
    };
  }

  async function save() {
    setError(null);
    if (!form.first_name.trim()) return setError('First name is required.');
    if (!form.last_name.trim())  return setError('Last name is required.');
    if (!form.email.trim())      return setError('Email is required.');
    if (!isEdit && form.password.length < PASSWORD_MIN) {
      return setError(`Password must be at least ${PASSWORD_MIN} characters.`);
    }
    setSaving(true);
    try {
      const payload = {
        first_name:   form.first_name,
        last_name:    form.last_name,
        email:        form.email,
        title:        form.title,
        calendly_url: form.calendly_url.trim() || null,
        role:         form.role
      };
      if (!isEdit) payload.password = form.password;
      if (isEdit) {
        await axios.put(`/api/users/${initial.id}`, payload);
      } else {
        await axios.post('/api/users', payload);
      }
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  // Defensive: prevent the form from offering a role change away
  // from admin on your own row. Server enforces the same rule.
  const cannotRevokeOwnAdmin = isEdit && currentUserId === initial?.id && initial?.role === 'admin';

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div className="modal-card modal-narrow" role="dialog" aria-modal="true" aria-label={isEdit ? 'Edit user' : 'Add user'}>
        <header className="modal-head">
          <h3>{isEdit ? `Edit ${initial?.name || 'User'}` : 'Add User'}</h3>
          <button type="button" className="modal-close" onClick={onClose} disabled={saving} aria-label="Close">×</button>
        </header>
        <div className="modal-body">
          <div className="form-grid">
            <label className="form-field">
              <span>First Name <span className="req">*</span></span>
              <input value={form.first_name} onChange={update('first_name')} autoFocus />
            </label>
            <label className="form-field">
              <span>Last Name <span className="req">*</span></span>
              <input value={form.last_name} onChange={update('last_name')} />
            </label>
            <label className="form-field form-field-wide">
              <span>Email <span className="req">*</span></span>
              <input type="email" value={form.email} onChange={update('email')} className="cell-mono" />
            </label>
            <label className="form-field form-field-wide">
              <span>Title / Position</span>
              <input value={form.title} onChange={update('title')} placeholder="e.g. Managing Director" />
            </label>
            <label className="form-field form-field-wide">
              <span>Calendly URL</span>
              <input
                value={form.calendly_url}
                onChange={update('calendly_url')}
                placeholder="https://calendly.com/your-name/15min"
                className="cell-mono"
                type="url"
              />
              <em style={{ color: 'var(--color-text-dim)', fontSize: 'var(--text-caption)' }}>
                Used by outreach email templates — fills the {'{{owner_calendly_url}}'} merge token.
              </em>
            </label>
            {!isEdit && (
              <label className="form-field form-field-wide">
                <span>Password <span className="req">*</span></span>
                <input
                  type="password"
                  value={form.password}
                  onChange={update('password')}
                  placeholder={`At least ${PASSWORD_MIN} characters`}
                />
              </label>
            )}
            <label className="form-field form-field-wide">
              <span>Role <span className="req">*</span></span>
              <select
                value={form.role}
                onChange={update('role')}
                disabled={cannotRevokeOwnAdmin}
              >
                <option value="admin">Admin — full access + user management</option>
                <option value="staff">Staff — full access except user management</option>
                <option value="salesperson">Salesperson — sales leads only</option>
              </select>
              {cannotRevokeOwnAdmin && (
                <em style={{ color: 'var(--color-text-dim)' }}>
                  You can't change your own role away from Admin.
                </em>
              )}
            </label>
          </div>

          {error && <p className="error">{error}</p>}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create User')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordDialog({ user, onClose, onSaved }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  async function save() {
    setError(null);
    if (password.length < PASSWORD_MIN) return setError(`Password must be at least ${PASSWORD_MIN} characters.`);
    if (password !== confirm)           return setError('Passwords do not match.');
    setSaving(true);
    try {
      await axios.post(`/api/users/${user.id}/password`, { password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div className="modal-card modal-narrow" role="dialog" aria-modal="true" aria-label="Reset password">
        <header className="modal-head">
          <h3>Reset Password — {user.name}</h3>
          <button type="button" className="modal-close" onClick={onClose} disabled={saving} aria-label="Close">×</button>
        </header>
        <div className="modal-body">
          {done ? (
            <>
              <div className="quote-success">
                <div className="quote-success-icon">✓</div>
                <h4>Password reset</h4>
                <p>Send the new password to <strong>{user.name}</strong> over a secure channel.</p>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-primary" onClick={() => { onSaved(); onClose(); }}>Done</button>
              </div>
            </>
          ) : (
            <>
              <p className="modal-sub">
                Set a new password for <strong>{user.name}</strong>.
                Minimum {PASSWORD_MIN} characters. The user's existing
                token stays valid until it expires — they can sign back
                in with the new password.
              </p>
              <div className="form-grid">
                <label className="form-field form-field-wide">
                  <span>New Password</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                  />
                </label>
                <label className="form-field form-field-wide">
                  <span>Confirm</span>
                  <input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                </label>
              </div>
              {error && <p className="error">{error}</p>}
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : 'Reset Password'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Two-step confirm dialog for user deletion.
//   Step 1: fetch /api/users/:id/delete-impact -> shows the user
//           card + the counts of records that will be SET NULL'd.
//   Step 2: typed-email confirmation guards against accidental
//           clicks. The Delete button stays disabled until the
//           input matches the user's email exactly (case-insensitive).
// Server enforces: cannot delete self, cannot delete last admin.
// Either guard surfaces as an error in this dialog.
function DeleteUserDialog({ user, onClose, onDeleted }) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`/api/users/${user.id}/delete-impact`)
      .then((r) => { if (!cancelled) setImpact(r.data?.impact || null); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user.id]);

  const canDelete = confirmText.trim().toLowerCase() === String(user.email || '').trim().toLowerCase()
    && !deleting && !loading && !error;

  async function performDelete() {
    setDeleting(true);
    setError(null);
    try {
      await axios.delete(`/api/users/${user.id}`);
      onDeleted();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setDeleting(false);
    }
  }

  // Friendly labels matching the impact object's keys.
  const ROWS = [
    ['owned_leads',              'Sales leads owned'],
    ['notes_authored',           'Notes authored'],
    ['tickets_primary_assignee', 'Tickets (primary owner)'],
    ['tickets_created',          'Tickets created'],
    ['ticket_notes_authored',    'Ticket comments authored'],
    ['documents_sent',           'Emails / quotes / Bulloch sent'],
    ['activity_log_entries',     'Activity log entries'],
    ['configurations_entered',   'Bulloch configurations entered']
  ];

  const total = impact
    ? Object.values(impact).reduce((s, n) => s + Number(n || 0), 0)
    : 0;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card modal-narrow"
        role="dialog"
        aria-modal="true"
        aria-label="Delete user"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 520 }}
      >
        <h3 style={{ marginTop: 0, color: 'var(--color-bad, #b91c1c)' }}>
          Delete user — {user.name || user.email}
        </h3>
        <p style={{ color: 'var(--color-text-dim)' }}>
          This permanently removes the user account. Records the user
          owned (leads, notes, tickets, sent emails) are kept; the
          owner / author / assignee column becomes <code>NULL</code>{' '}
          and the UI renders <em>(deleted user)</em> in their place.
        </p>

        {loading && <p>Loading impact…</p>}
        {error && <p className="error">{error}</p>}

        {!loading && impact && (
          <>
            <div style={{
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-3)',
              marginBottom: 'var(--space-3)'
            }}>
              <div style={{ fontWeight: 'var(--font-semibold)', marginBottom: 'var(--space-2)' }}>
                Records that will be set to (deleted user):
              </div>
              {total === 0
                ? <div className="result-count">None — this user has no owned records.</div>
                : ROWS.filter(([key]) => impact[key] > 0).map(([key, label]) => (
                    <div key={key} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                      <span>{label}</span>
                      <span className="cell-mono" style={{ fontWeight: 'var(--font-semibold)' }}>{impact[key]}</span>
                    </div>
                  ))}
            </div>

            <label style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
              <div style={{ fontWeight: 'var(--font-semibold)', marginBottom: 4 }}>
                Type the user's email to confirm:
              </div>
              <div style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)', marginBottom: 4 }}>
                <code>{user.email}</code>
              </div>
              <input
                type="text"
                autoFocus
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="email address"
                style={{ width: '100%' }}
              />
            </label>

            <div className="form-actions" style={{ marginTop: 'var(--space-3)' }}>
              <button type="button" className="btn-secondary" onClick={onClose} disabled={deleting}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={performDelete}
                disabled={!canDelete}
                style={{ background: canDelete ? 'var(--color-bad, #b91c1c)' : undefined }}
              >
                {deleting ? 'Deleting…' : 'Delete User'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
